import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';

// Define keyframes as a string that will be injected into a style tag
const spinKeyframes = `
  @keyframes bb-spinner-spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

export const BBSpinner = forwardRef(
  (
    {
      size = 'md',
      color = '#EF4444',
      trackColor = '#1F2937',
      speed = 1.2,
      isCentered = false,
      message = null,
      ...props
    },
    ref
  ) => {
    // Size mappings
    const sizeMap = {
      xs: 24,
      sm: 40,
      md: 56,
      lg: 72,
      xl: 96,
    };

    const spinnerSize = sizeMap[size] || sizeMap.md;
    const radius = (spinnerSize - 8) / 2; // Account for stroke width
    const center = spinnerSize / 2;

    const spinnerContent = (
      <>
        {/* Inject keyframes into document head */}
        <style>{spinKeyframes}</style>
        <Box
          ref={ref}
          sx={{
            display: 'inline-block',
            position: 'relative',
            width: `${spinnerSize}px`,
            height: `${spinnerSize}px`,
            animation: `bb-spinner-spin ${speed}s cubic-bezier(0.4, 0, 0.2, 1) infinite`,
            ...(!isCentered && props.sx),
          }}
          {...(!isCentered && props)}
        >
          <svg
            viewBox={`0 0 ${spinnerSize} ${spinnerSize}`}
            xmlns="http://www.w3.org/2000/svg"
            style={{
              width: '100%',
              height: '100%',
              transform: 'rotate(-90deg)',
            }}
          >
            {/* Track circle */}
            <circle
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke={trackColor}
              strokeWidth="4"
            />
            {/* Colored notch */}
            <circle
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke={color}
              strokeWidth="4"
              strokeDasharray="25 126"
              strokeDashoffset="0"
              strokeLinecap="round"
            />
          </svg>
        </Box>
      </>
    );

    // If centered, wrap in a centering container
    if (isCentered) {
      // Calculate offset to keep spinner centered when message appears
      // Message line-height is 16px, gap is 2 (16px in MUI), so total offset is ~32px
      const messageOffset = message ? '16px' : '0px';

      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            width: '100%',
            backgroundColor: '#FFF',
            gap: 2,
            ...props.sx,
          }}
          {...props}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              transform: `translateY(${messageOffset})`,
              // transition: 'transform 0.2s ease-in-out',
            }}
          >
            {spinnerContent}
            {message && (
              <Box
                sx={{
                  fontSize: '16px',
                  color: '#64748b',
                  fontFamily:
                    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                }}
              >
                {message}
              </Box>
            )}
          </Box>
        </Box>
      );
    }

    return spinnerContent;
  }
);

BBSpinner.displayName = 'BBSpinner';

BBSpinner.propTypes = {
  /**
   * The size of the spinner
   */
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
  /**
   * The color of the colored notch (hex, rgb, or named color)
   */
  color: PropTypes.string,
  /**
   * The color of the track circle (hex, rgb, or named color)
   */
  trackColor: PropTypes.string,
  /**
   * The speed of the spin animation in seconds
   */
  speed: PropTypes.number,
  /**
   * Whether to center the spinner vertically and horizontally on the page
   */
  isCentered: PropTypes.bool,
  /**
   * Optional message to display below the spinner (only shown when isCentered is true)
   */
  message: PropTypes.string,
  /**
   * MUI sx prop for additional styling
   */
  sx: PropTypes.object,
};
